import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Article, PostCategory } from '@/entities';
import { ImageShape } from '@/shared/lib/types';
import { formatDate } from '@/shared/lib/utils/dates';
import { axiosAPI } from './axios-instance';
import { ApiRequestFnParams, ApiResponse } from './lib/types';

export type ApiNewsRequest = Partial<{
    categories: number[];
    limit: number;
    page: number;
    not_id: number;
    order: string;
    q: string;
    sort: string;
}>;

type Params = ApiRequestFnParams<ApiNewsRequest> & {
    postCategories: PostCategory[];
};

export type ApiNewsResponse = ApiResponse<{
    totalCount: number;
    items: {
        id: number;
        slug: string;
        name: string;
        published_at: string;
        categoryIds?: number[] | null;
        image?: ImageShape | null;
        description?: string | null;
    }[];
}>;

export type AppNewsResponse = ApiResponse<{
    items: Article[];
    totalCount: number;
}>;

const transformApiResponse = (response: ApiNewsResponse, postCategories: PostCategory[]): AppNewsResponse => {
    return {
        ...response,
        data: {
            totalCount: response.data?.totalCount || 0,
            items: (response.data?.items ?? []).map((item) => ({
                id: item.id,
                title: item.name,
                date: formatDate(new Date(item.published_at)),
                img: item.image || null,
                slug: item.slug,
                categories: (item.categoryIds || []).map((id) => {
                    const matchingCategory = postCategories.find((category) => category.id === id);

                    return {
                        id: matchingCategory?.id || -1,
                        slug: matchingCategory?.slug || '',
                        name: matchingCategory?.name || '',
                    };
                }),
            })),
        },
    };
};

export const getNews = ({ params = {}, postCategories }: Params): Promise<AppNewsResponse> =>
    axiosAPI
        .get<ApiNewsResponse>('/v1/posts', { params })
        .then((res) => transformApiResponse(res.data, postCategories));

export const useNewsQuery = (data: Params, initialData?: AppNewsResponse) =>
    useQuery({
        queryKey: ['news', data],
        queryFn: () => getNews(data),
        initialData,
        placeholderData: keepPreviousData,
    });
